import StarzplayerConstants from './starzplayer_constants';
import Logger from './util/logger_utils';
import async from 'async';
import {ClientErrorHandler, ErrorHandler, DEFAULT_ERROR, CLIENT_ERRORS} from './modules/error_handler';
import Splunk from './modules/report/splunk';
import 'url-polyfill';
import {Concurrency, Errors} from './modules/concurrency';
//import {Concurrency, Errors as ThePlatformErrors} from './modules/the_platform';
import URLHelper from 'url';
import config from './config';

const Modules = {
    StarzPlayerConfiguration: require('./starzplayer_configuration'), // require
    Vualto: require('./modules/vualto/vualto'),
    Feed: require('./modules/feed/feed'),
    Browser: require('./modules/browser/browser'),
    LanguageManager: require('./modules/language_manager/language_manager').default,
    MediaList: require('./modules/media_list/media_list'),
    Splunk: require('./modules/report/splunk').default,
    Youbora: require('./modules/report/youbora').default,
    AgeAdvisor: require('./modules/age_advisor/age_advisor'),
    Preferences: require('./util/preferences_utils.js').default,
};

const Adapters = {
    BitmovinAdapter: require('./adapters/bitmovin_adapter')
};

const EventHandlers = require('./event_handlers/event_handlers');

let feed;
let startAt;
let episode;
let season;
let latest;
let sessionToken;
let mpxToken;
let adapter;
let language;
let videoType;
let css;
let fullScreen;
let starzplayer;
let eventHandlers;
let clientHandlers;
let isReady = false;
let recEngineModuleId;
let parentalControl;
let start;
let end;
let packageName;
let concurrency;
let isLive;
let packageParent;
let subscriptionIncludes;
let sessionId;
let enableConcurrency;

/**
 * @parm {Object} userConfig
 * @param [userConfig.videoType]
 * @param userConfig.feed - The Feed from the content to play
 * @param userConfig.sessionToken - The token from GIGYA
 * @param userConfig.fullScreen
 * @param userConfig.adapter
 * @param [userConfig.season]
 * @param [userConfig.episode]
 * @param [userConfig.latest=false] Play the latest episode for a series
 * @param [userConfig.startAt]
 * @param [userConfig.css] adapter specific userConfig to specify a css file.
 * @param [userConfig.eventHandlers]
 * @param [userConfig.language="en"]
 * @param [userConfig.recEngineModuleId=undefined]
 * @param [userConfig.parentalControl=undefined]
 * @param {Boolean} [userConfig.forceFlash=false]
 * @param [userConfig.start=undefined]
 * @param [userConfig.end=undefined]
 * @constructor
 * @Singleton
 */
function StarzPlayer(userConfig) {
    Logger.debug(userConfig);
    if (adapter) throw Error('Cannot initialize player again. Use .reinitialize() instead');
    adapter = userConfig.adapter || new Adapters.BitmovinAdapter();

    ErrorHandler.addOptions({adapter: adapter});
    Splunk.addOptions({
        adapter: adapter,
        feed: userConfig.feed,
        videoType: userConfig.videoType,
        sessionToken: userConfig.sessionToken,
        clientConfiguration: userConfig
    });

    starzplayer = this;

    async.waterfall([
        (callback) => startLoadingAnimation(callback),
        (callback) => populatePlayerVariables(userConfig, callback),
        (callback) => initializeModules(callback),
        (callback) => parseURIData(callback),
        (parsedUri, callback) => prepareData(parsedUri, callback),
        (parsedUri, callback) => parseFeed(parsedUri, callback),
        (feedInfoArray, callback) => createDrmConfigurations(feedInfoArray, callback),
        (feedInfoArray, drmInfo, callback) => createConfiguration(feedInfoArray, drmInfo, callback),
        (playerConfig, callback) => {
            Modules.Youbora.hookAnalytics(adapter, playerConfig);
            callback(null, playerConfig);
        },
        (playerConfig, callback) => registerEventHandlers(playerConfig, callback),
        (playerConfig, callback) => validate(playerConfig, callback)
    ], function(err, playerConfig) {
        Logger.debug('StarzPlayer Initialized with arguments:', playerConfig);
        if (err) {
            onError(playerConfig);
        } else {
            initializePlayer(playerConfig);
        }
        isReady = true;
    });
}

/**
 * Reinitialize the player
 * @param {Object} userConfig
 */
StarzPlayer.prototype.reinitialize = function(userConfig) {
    let splitPath = window.location.pathname.split('/');
    splitPath[splitPath.length - 1] = `s${userConfig.season}e${userConfig.episode}`;
    let url = `${splitPath.join('/')}?play=${btoa(JSON.stringify(userConfig))}&parentalRating=${userConfig.parentalControl}&recEngineModuleIs=${userConfig.recEngineModuleId}`;
    document.location.replace(url);
};

function resetPlayer(callback) {
    if (isReady) {
        adapter && adapter.unload();
        adapter.removeEventHandlers();
        eventHandlers && eventHandlers.onReinit();
        eventHandlers && eventHandlers.onDestroy();
        callback(null);
    } else {
        setTimeout(() => resetPlayer(callback), 100);
    }
}

function populatePlayerVariables(userConfig, callback) {
    const spConfig = window.sessionStorage.getItem('SPConf');
    const configuration = JSON.parse(atob(spConfig));
    const userInfo = atob(configuration.playKeyEncoded).split(',');
    videoType = userConfig.videoType;
    feed = userConfig.feed;
    mpxToken = userConfig.mpxToken;
    sessionToken = userConfig.sessionToken;
    clientHandlers = userConfig.eventHandlers;
    season = userConfig.season;
    episode = userConfig.episode;
    latest = userConfig.latest || false;
    startAt = userConfig.startAt || 0;
    css = userConfig.css || undefined;
    fullScreen = userConfig.fullScreen || false;
    language = userConfig.language || 'en';
    recEngineModuleId = userConfig.recEngineModuleId;
    parentalControl = userConfig.parentalControl;
    start = userConfig.start;
    end = userConfig.end;
    packageName = userConfig.packageName;
    concurrency = userConfig.concurrency || false;
    isLive = userConfig.isLive;
    packageParent = userConfig.packageParent;
    subscriptionIncludes = userConfig.subscriptionIncludes;
    sessionId = userConfig.sessionId;
    enableConcurrency = userConfig.enableConcurrency;
    if (!!userConfig.lbCDN) {
        // FIXME: temp fix for the lebenon CDN, may get removed in future
        window.sessionStorage.setItem('lbCDN_Host', 'https://cedar01-ipv4.starzplayarabia.com');
    } else {
        window.sessionStorage.removeItem('lbCDN_Host');
    }

    callback(null);
}

function initializeModules(callback) {
    Modules.LanguageManager.setLanguage(language);

    callback(null);
}

function startLoadingAnimation(callback) {
    Modules.AgeAdvisor.showLoader();
    callback(null);
}

function prepareData(parsedUri, callback) {
    if (videoType === 'trailer') return callback(null, parsedUri);
    async.parallel([
        // Generate MPX token for this session
        (callback) => {
            Modules.Vualto.getMpxToken({globalUserId: parsedUri.globalUserId, sessionToken}, (success, token) => {
                mpxToken = token;
                callback();
            });
        },
        // Get the progress for the contents
        (callback) => {
            if (videoType === 'trailer') return callback(null);
            Modules.MediaList.getProgressInfo((success, progress) => {
                season = progress.season;
                episode = progress.episode;
                startAt = progress.playbackTime;
                callback(!success);
            }, {
                season: season,
                episode: episode,
                videoType: videoType,
                latest: latest,
                contentId: parsedUri.contentId,
                userId: parsedUri.globalUserId,
                globalUserId: parsedUri.globalUserId,
                sessionToken: sessionToken,
                enableConcurrency: enableConcurrency
            });
        }

    ], err => {
        err ? ErrorHandler.throwError(DEFAULT_ERROR, {
            reason: 'Couldnt pass through prepareData() function',
            error: err
        }) : callback(null, parsedUri);
    });
}

/**
 * Parses information from URI
 * @param {Function} callback
 * @param {Boolean} callback.error
 * @param {Object|String} callback.parsedUri, string contains error
 * @param {String} callback.parsedUri.videoType
 * @param {String} callback.parsedUri.globalUserId
 * @param {Number} callback.parsedUri.contentId
 * @param {String} callback.parsedUri.lang
 */
function parseURIData(callback) {
    const uriSplit = feed.split('/');
    const feedUrl = URLHelper.parse(feed, true);
    const queryObj = feedUrl.query;
    let globalUserId = queryObj.userId || queryObj.globalUserId;

    if (!globalUserId) {
        const spConfig = window.sessionStorage.getItem('SPConf');
        try {
            const configuration = JSON.parse(atob(spConfig));
            const userInfo = atob(configuration.playKeyEncoded).split(',');
            globalUserId = userInfo[0];
        } catch (err) {}
    }


    const parsedUri = {
        videoType: uriSplit[7],
        globalUserId: globalUserId,
        userId: globalUserId,
        parentalControl: queryObj.parentalControl || (globalUserId && 18),
        contentId: uriSplit[8].indexOf('?') ? uriSplit[8].split('?')[0] : uriSplit[8],
        lang: queryObj.lang || 'en',
        start: queryObj.start,
        end: queryObj.end
    };

    // Test input
    if ([StarzplayerConstants.SERIES, StarzplayerConstants.MOVIE].indexOf(parsedUri.videoType) === -1) {
        callback(true, 'Invalid videoType "' + videoType + '" in URI: ' + feed);
    } else if (!parsedUri.globalUserId && videoType !== StarzplayerConstants.TRAILER) {
        callback(true, 'userId is required for all content except trailers');
    } else if (!parsedUri.contentId || isNaN(parsedUri.contentId)) callback(true, 'invalid contentId. Given: ' + parsedUri.contentId);
    else callback(null, parsedUri);
}

/**
 * Parses the feed, and returns a feedInfo object.
 * @param parsedUri
 * @param {Function} callback - async.waterfall callback
 * @param {any} callback.error - null in case of successful callback
 * @param {Object} callback.feedInfo
 */

function parseFeed(parsedUri, callback) {
    Logger.debug('Parsed URI Info: ', parsedUri);
    Logger.debug('parsing feed');

    // FIXME This is not the correct place to have this feed update
    const assetTypeOptions = {
        videoType: parsedUri.videoType,
        trailer: videoType === StarzplayerConstants.TRAILER
    };
    feed = Modules.Browser.attachAssetTypesFilterParamsToUri(feed, assetTypeOptions);

    Modules.Feed.parse(feed,
        {
            trailer: videoType === StarzplayerConstants.TRAILER,
            videoType: parsedUri.videoType,
            globalUserId: parsedUri.globalUserId,
            userId: parsedUri.globalUserId,
            season: season,
            episode: episode,
            latest: latest,
            start: parsedUri.start,
            end: parsedUri.end,
            packageName: parsedUri.packageName,
            isLive: parsedUri.isLive,
            sessionId: parsedUri.sessionId,
            enableConcurrency: parsedUri.enableConcurrency
        },
        function(success, feedInfo) {
            success ? callback(null, feedInfo) : callback(true, feedInfo);
        }
    );
}

/**
 * @param {Object} feedInfoArray the Feedinfo provided by parseFeed()
 * @param {Function} callback - async.waterfall callback
 * @param {any} callback.error - null in case of successful callback
 * @param {Object} callback.feedInfoArray - the feedInfo provided by parseFeed
 * @param {Object} callback.drmConfigurations - object with the drmConfigurations, asJson()
 */
function createDrmConfigurations(feedInfoArray, callback) {
    var callbackCalled = false;
    var drmConfigurations = {};

    // Todo: make flatter
    if (typeof (videoType) !== 'undefined' && videoType === StarzplayerConstants.TRAILER) {
        // NO DRM required
        callback(null, feedInfoArray, false);
    } else {
        var toExecute = Object.keys(feedInfoArray.drm).length - 1;
        for (var key in feedInfoArray.drm) {
            if (feedInfoArray.drm.hasOwnProperty(key)) {
                var drmObject = feedInfoArray.drm[key];
                Modules.Vualto.createDrmConfiguration(sessionToken, key, mpxToken, feedInfoArray.globalUserId, drmObject.pid,
                    function(success, drmConfiguration) {
                        if (success) {
                            drmConfigurations[drmConfiguration.type] = drmConfiguration.asJson();
                            toExecute === 0 ? callback(null, feedInfoArray, drmConfigurations) : toExecute--;
                        } else {
                            if (!callbackCalled) {
                                Logger.error(typeof drmConfiguration === 'string' ? drmConfiguration : JSON.stringify(drmConfiguration));

                                callback(true, 'Something went wrong while creating the DRM Configuration');
                                callbackCalled = true;
                            }
                        }
                    });
            }
        }
    }
}

/**
 * Creates the configuration based on the feed and DRM info provided by parseFeed() and createDrmConfigurations()
 * @param {Object} feedInfoArray provided parseFeed();
 * @param {Object} drmInfo provided by createDrmConfigurations()
 * @param {Function} callback - async.waterfall callback
 * @param {any} callback.error - null in case of successful callback
 * @param {StarzPlayerConfiguration} callback.playerConfig - contains the configuration for the player, or the error message in case of error
 */
function createConfiguration(feedInfoArray, drmInfo, callback) {
    Logger.debug('FeedInfoArray', feedInfoArray);
    Logger.debug('drmInfo', drmInfo);

    // modify host for filmstrip URL
    let filmstripUrl = feedInfoArray.filmstrip && new URL(feedInfoArray.filmstrip);
    let httpsCdnUrlVal = (filmstripUrl.href && filmstripUrl.href.indexOf(config.httpsCdnUrl) > -1) ? config.httpsCdnUrl : config.httpsCdnUrl2;
    let httpsCdnUrl = httpsCdnUrlVal && new URL(httpsCdnUrlVal);
    if (feedInfoArray.filmstrip && config.httpsCdnUrl) {
        filmstripUrl.hostname = httpsCdnUrl.hostname;
        filmstripUrl.protocol = 'https';
        filmstripUrl = filmstripUrl.href;
    }

    var playerConfiguration = new Modules.StarzPlayerConfiguration({
        src: feedInfoArray.url,
        drm: drmInfo,
        adapter: {
            css: css
        },
        content: {
            fullScreen: fullScreen,
            filmstripUri: filmstripUrl,
            feed: feed,
            videoType: feedInfoArray.videoType,
            startAt: startAt,
            id: feedInfoArray.titleId,
            title: feedInfoArray.title,
            trailer: feedInfoArray.trailer || false,
            language: language,
            series: (feedInfoArray.videoType === StarzplayerConstants.SERIES ? {
                seriesId: feedInfoArray['seriesFeedInfo']['seriesId'],
                seriesName: feedInfoArray['seriesFeedInfo']['title'],
                episode: feedInfoArray['seriesFeedInfo']['episode'],
                season: feedInfoArray['seriesFeedInfo']['season'],
                seasons: feedInfoArray['seriesFeedInfo']['seasons'],
                nextEpisode: feedInfoArray['seriesFeedInfo']['nextEpisode'],
                nextSeason: feedInfoArray['seriesFeedInfo']['nextSeason'],
                latest: latest
            } : {}),
            shortDescription: feedInfoArray.shortDescription,
            longDescription: feedInfoArray.longDescription,
            ageRestriction: feedInfoArray.ageRating,
            analyticsInfo: feedInfoArray.analyticsInfo,
            selectorServiceUrl: feedInfoArray.drm[Object.keys(feedInfoArray.drm)[0]].selectorServiceURL,
            resource: feedInfoArray.resource
        },
        user: {
            globalUserId: feedInfoArray.globalUserId,
            sessionToken: sessionToken,
            mpxToken: mpxToken,
            packageName: packageName,
            concurrency: concurrency,
            isLive: isLive,
            packageParent: packageParent,
            subscriptionIncludes: subscriptionIncludes,
            sessionId: sessionId,
            enableConcurrency: enableConcurrency
        },
        recEngine: {
            moduleId: recEngineModuleId,
            parentalControl: parentalControl,
            start: start,
            end: end
        }
    });
    Splunk.addOptions({
        starzplayerConfiguration: playerConfiguration,
        userId: feedInfoArray.globalUserId
    });

    //fetch User Preferences
    const Preferences = new Modules.Preferences({
        userId: feedInfoArray.globalUserId,
        sessionToken: sessionToken
    });
    Preferences.get((success, preferences) => {
        if(success && preferences){
            if (Object.keys(preferences).length !== 0){
                Preferences.setInLocalStorage(preferences);
            }
        }
    })

    callback(null, playerConfiguration);
}

function registerEventHandlers(playerConfig, callback) {

    var defaultBehaviour = function() {
    };

    var options = {
        starzplayerInstance: starzplayer,
        feed: feed,
        clientHandlers: clientHandlers,
        sessionToken: sessionToken
    };
    const eventHandlersType = playerConfig.content.trailer ? StarzplayerConstants.TRAILER : playerConfig.content.videoType;
    var eventHandlersForType = EventHandlers[eventHandlersType](playerConfig, adapter, options);

    eventHandlers = {
        onInit: eventHandlersForType.onInit || defaultBehaviour,
        onPlay: eventHandlersForType.onPlay || defaultBehaviour,
        onPause: eventHandlersForType.onPause || defaultBehaviour,
        onError: onError,
        onReady: eventHandlersForType.onReady || defaultBehaviour,
        onPlaybackFinished: eventHandlersForType.onPlaybackFinished || defaultBehaviour,
        onTest: eventHandlersForType.onPlaybackFinished || defaultBehaviour,
        onSubtitleChange: eventHandlersForType.onSubtitleChange || defaultBehaviour,
        onSubtitleAdded: eventHandlersForType.onSubtitleAdded || defaultBehaviour,
        onAudioChange: eventHandlersForType.onAudioChange || defaultBehaviour,
        onShowControls: eventHandlersForType.onShowControls || defaultBehaviour,
        onHideControls: eventHandlersForType.onHideControls || defaultBehaviour,
        onSeek: eventHandlersForType.onSeek || defaultBehaviour,

        onCueEnter: eventHandlersForType.onCueEnter || defaultBehaviour,
        onCueExit: eventHandlersForType.onCueExit || defaultBehaviour,

        onDestroy: eventHandlersForType.onDestroy || defaultBehaviour,
        onReinit: eventHandlersForType.onReinit || defaultBehaviour,

        onTimeChange: eventHandlersForType.onTimeChange || defaultBehaviour,

        onVolumeChange: eventHandlersForType.onVolumeChange || defaultBehaviour
    };

    callback(null, playerConfig);
}

/**
 * Checks whether the current configuration is valid.
 * @param eventHandlers
 * @param playerConfig
 * @param callback
 */
function validate(playerConfig, callback) {
    if (!playerConfig.src) {
        // ErrorHandler.throwError(ThePlatformErrors.INVALID_ASSET_TYPE, {
        //     reason: 'There is no source found. Check the player configuration'
        // });
    }

    if (videoType !== 'trailer') {
        // Concurrency check
        if (playerConfig) {
            Concurrency.initialize({
                selectorServiceUrl: playerConfig.content.selectorServiceUrl,
                globalUserId: playerConfig.user.globalUserId,
                userId: playerConfig.user.globalUserId,
                mpxToken: playerConfig.user.mpxToken,
                sessionToken: playerConfig.user.sessionToken,
                packageName: playerConfig.user.packageName,
                concurrency: playerConfig.user.concurrency,
                subscriptionIncludes: playerConfig.user.subscriptionIncludes,
                sessionId: playerConfig.user.sessionId,
                packageParent: playerConfig.user.packageParent,
                enableConcurrency: playerConfig.user.enableConcurrency
            }, () => {
                callback(null, playerConfig);
            });
        }
    } else {
        callback(null, playerConfig);
    }
}

/**
 * This function will initialize the player by adding events and running initialize on the player.
 * @param playerEventHandlers
 * @param {StarzPlayerConfiguration} playerConfig - The player configuration
 */
function initializePlayer(playerConfig) {
    if (!checkPlatform(adapter)) {
        return false;
    }
    // Initialize
    adapter.initialize(playerConfig, eventHandlers, {reinitialize: isReady});
    eventHandlers.onInit();
}

/**
 * Function to process and handle errors.
 * @param error
 */
function onError(error) {
    const isBitmovinError = typeof error === 'object' && error.code;
    if (isBitmovinError && adapter) {
        ErrorHandler.throwError(adapter.mapErrorCode(error.code) || DEFAULT_ERROR, error);
    } else {
        ErrorHandler.throwError(DEFAULT_ERROR, error);
    }
}

function checkPlatform(adapter) {
    // FIXME This is player specific, it should not be here. The adapter should expose a common API instead
    var supported = adapter.player.getSupportedTech();
    var returnType = true;

    // Remove flash from the SupportedTech array
    // since we are not going to have the flash fallback
    supported = supported.map(function(i) {
        return i.player !== 'flash' ? i : '';
    }).filter(function(o) {
        return o !== '';
    });

    if (supported.length < 1) {
        ErrorHandler.throwError({
            code: 571112,
            message: Modules.LanguageManager.get('error_unsupported_platform')
        });
        returnType = false;
    }

    return returnType;
}

// Expose to clients
export {
    DEFAULT_ERROR,
    CLIENT_ERRORS,
    ClientErrorHandler as ErrorHandler,
    StarzPlayer
};



// WEBPACK FOOTER //
// ./src/starzplayer.js